import { preselectSeriesFromUrl } from './downloadsSeriePreselect.js';

/**
 * Downloadcenter-Kollektion: Bereich-Segmented + Produktserie-Filter +
 * Freitextsuche + Lazy Reveal (Endless Scroll) + Teilen.
 *
 * - Bereich-Segmented (Pill-Indicator-Muster wie ansprechpartner.js): setzt den
 *   aktiven Bereich, verschiebt das Pill und blendet in der Sidebar nur die
 *   Produktserien des aktiven Bereichs ein.
 * - Produktserie-Checkboxen: Mehrfachauswahl (Schnittmenge mit Karten-Serien).
 * - Suche: debounced, filtert nach Dateiname (data-title).
 * - Lazy Reveal: IntersectionObserver auf einen Sentinel blendet die Treffer
 *   blockweise ein (visibleCount += N), statt alle auf einmal zu rendern.
 * - Server-Pagination aufloesen: die Kollektion wird vom Core ab
 *   'entries_per_page' Eintraegen serverseitig paginiert (?page=N) - Filter/
 *   Suche laufen aber clientseitig und wuerden nur die aktuelle Seite sehen.
 *   Deshalb werden die uebrigen Seiten im Hintergrund nachgeladen, Karten und
 *   Sidebar-Filter (identisches Server-Markup) gemergt und die Pagination
 *   anschliessend entfernt: alles bleibt auf EINER Seite, das Lazy Reveal
 *   uebernimmt das Einblenden beim Scrollen.
 * - Teilen: navigator.share, Fallback navigator.clipboard + kurzer Toast.
 *
 * Vanilla JS, kein jQuery.
 */
import { queuePdfPreview } from './pdfPreview.js';

export function initDownloads() {
    const BLOCK = 12; // Anzahl Karten pro Lazy-Reveal-Block

    document.querySelectorAll('.js-downloads').forEach(root => {
        const hero         = root.querySelector('.js-downloadsHero');
        const heroLayers   = Array.from(root.querySelectorAll('.js-downloadsHeroLayer'));
        const bereichNav   = root.querySelector('.js-downloadsBereich');
        const bereichTabs  = Array.from(root.querySelectorAll('.js-downloadsBereichTab'));
        const pill         = root.querySelector('.downloads__bereichIndicator');
        // let statt const: beim Nachladen weiterer Server-Seiten kommen
        // Karten und Sidebar-Eintraege dazu (siehe loadRemainingPages).
        const serieItems     = Array.from(root.querySelectorAll('.js-downloadsSerieItem'));
        const serieBoxes   = Array.from(root.querySelectorAll('.js-downloadsSerie'));
        const typBoxes     = Array.from(root.querySelectorAll('.js-downloadsTyp'));
        const searchInput  = root.querySelector('.js-downloadsSearch');
        let cards          = Array.from(root.querySelectorAll('.js-downloadsCard'));
        const emptyMsg     = root.querySelector('.js-downloadsEmpty');
        const sentinel     = root.querySelector('.js-downloadsSentinel');
        const filterToggle = root.querySelector('.js-downloadsFilterToggle');
        const filterBody   = root.querySelector('.js-downloadsFilterBody');

        if (!cards.length) return;

        let activeBereich = '';
        const activeSeries = new Set();
        const activeTypes = new Set();
        let query = '';
        let visibleCount = BLOCK;

        // Reveal (Filter/Lazy-Reveal) heisst nicht "im Viewport": ein Block von
        // BLOCK Karten wird oft komplett enthuellt, obwohl nur ein Teil davon
        // ueber dem Fold liegt. Erst beim tatsaechlichen Naehern an den Viewport
        // die PDF-Vorschau anstossen, sonst konkurrieren unsichtbare Karten mit
        // sichtbaren um Bandbreite/CPU.
        const previewObserver = typeof IntersectionObserver !== 'undefined'
            ? new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        previewObserver.unobserve(entry.target);
                        ensurePreview(entry.target);
                    }
                });
            }, { rootMargin: '400px 0px' })
            : null;

        // Treffer (gefiltert, ungeachtet visibleCount) als Basis fuer Lazy Reveal.
        function matches(card) {
            const cardBereich = (card.dataset.bereich || '').split(';').filter(Boolean);
            const cardSerie   = (card.dataset.serie || '').split(';').filter(Boolean);
            const cardTyp     = (card.dataset.typ || '').split(';').filter(Boolean);
            const title       = card.dataset.title || '';

            const matchBereich = activeBereich === '' || cardBereich.includes(activeBereich);
            const matchSerie   = activeSeries.size === 0 || cardSerie.some(s => activeSeries.has(s));
            const matchTyp     = activeTypes.size === 0 || cardTyp.some(t => activeTypes.has(t));
            const matchQuery   = query === '' || title.indexOf(query) !== -1;

            return matchBereich && matchSerie && matchTyp && matchQuery;
        }

        // PDF-Vorschau (erste Seite) fuer eine sichtbare Karte erzeugen, sofern
        // kein gepflegtes Vorschaubild existiert und eine PDF verlinkt ist.
        function ensurePreview(card) {
            if (card.dataset.previewState) return; // bereits angestossen/erledigt
            if (card.dataset.hasPreview === '1') return; // CMS-Bild hat Vorrang

            const file = (card.dataset.file || '').trim();
            if (file === '' || !/\.pdf(\?|#|$)/i.test(file)) return;

            card.dataset.previewState = 'loading';
            const media = card.querySelector('.downloads__cardMedia');
            if (media) media.classList.add('is-loading');

            queuePdfPreview(file)
                .then(previewEl => {
                    if (!media) return;
                    const placeholder = media.querySelector('.downloads__cardPlaceholder');
                    if (placeholder) placeholder.remove();
                    // previewEl ist ein frisch gerendertes <canvas> oder, bei
                    // einem Treffer im persistenten Thumbnail-Cache, ein <img>.
                    previewEl.className = 'downloads__cardImg downloads__cardCanvas';
                    previewEl.setAttribute('aria-hidden', 'true');
                    media.appendChild(previewEl);
                    media.classList.remove('is-loading');
                    card.dataset.previewState = 'done';
                })
                .catch(() => {
                    if (media) media.classList.remove('is-loading');
                    card.dataset.previewState = 'error';
                });
        }

        function applyFilter() {
            let shown = 0;
            let total = 0;
            cards.forEach(card => {
                if (matches(card)) {
                    total++;
                    const reveal = shown < visibleCount;
                    card.hidden = !reveal;
                    if (reveal) {
                        shown++;
                        if (previewObserver) previewObserver.observe(card);
                        else ensurePreview(card);
                    }
                } else {
                    card.hidden = true;
                }
            });

            if (emptyMsg) emptyMsg.hidden = total !== 0;

            // Sentinel nur aktiv lassen, solange es noch ausgeblendete Treffer gibt.
            if (sentinel) {
                sentinel.dataset.more = total > shown ? '1' : '0';
            }
        }

        // Sidebar: nur Serien des aktiven Bereichs zeigen; abgewaehlte Serien droppen.
        function rebuildSeries() {
            serieItems.forEach(item => {
                const itemBereich = (item.dataset.bereich || '').split(';').filter(Boolean);
                const visible = activeBereich === '' || itemBereich.includes(activeBereich);
                item.hidden = !visible;
                if (!visible) {
                    const box = item.querySelector('.js-downloadsSerie');
                    if (box && box.checked) {
                        box.checked = false;
                        activeSeries.delete(box.value);
                    }
                }
            });
        }

        function movePill(tab) {
            if (!pill || !tab) return;
            pill.style.left = tab.offsetLeft + 'px';
            pill.style.width = tab.offsetWidth + 'px';
        }

        // Passende Banner-Ebene zum aktiven Bereich einblenden. Jede Ebene
        // (.js-downloadsHeroLayer) traegt ihr rohes Sitepart-<picture> und ihren
        // Bereich als data-bereich. Faellt auf die "Alle"-Ebene (data-bereich="")
        // zurueck, falls fuer den Bereich keine gepflegt ist. Das Ueberblenden
        // erledigt die Opacity-Transition im SCSS; die Alle-Ebene ist beim Laden
        // bereits serverseitig sichtbar, daher gibt es keinen Einblend-Ruckler.
        // Der animate-Parameter bleibt aus Kompatibilitaet erhalten, wird hier
        // aber nicht mehr gebraucht (der visuelle Effekt kommt rein aus CSS).
        function updateBanner(bereich) {
            if (!heroLayers.length) return;
            const target = heroLayers.find(l => l.dataset.bereich === bereich)
                || heroLayers.find(l => l.dataset.bereich === '')
                || heroLayers[0];
            heroLayers.forEach(l => l.classList.toggle('is-visible', l === target));
        }

        function activateBereich(tab, animate = true) {
            activeBereich = tab.dataset.bereich || '';
            bereichTabs.forEach(t => {
                const isActive = t === tab;
                t.classList.toggle('is-active', isActive);
                t.setAttribute('aria-selected', isActive ? 'true' : 'false');
            });

            if (!animate && pill) pill.style.transition = 'none';
            movePill(tab);
            if (!animate && pill) requestAnimationFrame(() => { pill.style.transition = ''; });

            rebuildSeries();
            visibleCount = BLOCK;
            applyFilter();
            updateBanner(activeBereich);
        }

        bereichTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                activateBereich(tab);
            });
        });

        // Als benannte Binder, damit auch nachgeladene Sidebar-Eintraege
        // (loadRemainingPages) dieselben Handler bekommen.
        function bindSerieBox(box) {
            box.addEventListener('change', () => {
                if (box.checked) {
                    activeSeries.add(box.value);
                } else {
                    activeSeries.delete(box.value);
                }
                visibleCount = BLOCK;
                applyFilter();
            });
        }

        function bindTypBox(box) {
            box.addEventListener('change', () => {
                if (box.checked) {
                    activeTypes.add(box.value);
                } else {
                    activeTypes.delete(box.value);
                }
                visibleCount = BLOCK;
                applyFilter();
            });
        }

        serieBoxes.forEach(bindSerieBox);
        typBoxes.forEach(bindTypBox);

        // Suche (debounced)
        if (searchInput) {
            let t = null;
            searchInput.addEventListener('input', () => {
                if (t) clearTimeout(t);
                t = setTimeout(() => {
                    query = searchInput.value.trim().toLowerCase();
                    visibleCount = BLOCK;
                    applyFilter();
                }, 150);
            });
        }

        // Nativer Fokus-Scroll unterdruecken: Die Serie-/Typ-Checkboxen sind
        // visuell versteckte, aber fokussierbare Inputs (position:absolute, 1px).
        // Klickt man einen Eintrag an, bekommt sein Input Fokus und der Browser
        // scrollt den overflow-Container (.filterBody) so, dass das 1px-Element
        // "im Blick" ist - die ganze Filterliste ruckt dann sichtbar nach oben.
        // Loesung: Scrollposition beim Pointerdown (vor dem Fokus-Scroll) merken
        // und im folgenden focusin wieder herstellen. Nur bei Maus/Touch, damit
        // Tastatur-Navigation (Tab) weiterhin normal zum Element scrollen darf.
        if (filterBody) {
            let restoreScroll = null;
            filterBody.addEventListener('pointerdown', () => {
                restoreScroll = filterBody.scrollTop;
            });
            filterBody.addEventListener('focusin', () => {
                if (restoreScroll !== null) {
                    filterBody.scrollTop = restoreScroll;
                    restoreScroll = null;
                }
            });
            filterBody.addEventListener('pointerup', () => {
                restoreScroll = null;
            });
        }

        // Mobile: Filter-Sidebar auf-/zuklappen
        if (filterToggle && filterBody) {
            filterToggle.addEventListener('click', () => {
                const open = root.classList.toggle('is-filter-open');
                filterToggle.setAttribute('aria-expanded', String(open));
            });
        }

        // Filtergruppen klappbar (slidecontent): NICHT hier initialisieren!
        // Das Parent-Theme (cronus-theme website main.js) ruft initSlidecontents()
        // bereits global fuer alle '.slidecontentItem__headline' auf und oeffnet
        // '.first-opened'. Ein zusaetzlicher Aufruf hier wuerde einen zweiten
        // Click-Listener binden -> doppelter Toggle.

        // Teilen: native Web Share API, Fallback Clipboard + Toast.
        // Benannter Binder, damit auch Share-Buttons nachgeladener Karten
        // (loadRemainingPages) den Handler bekommen.
        function bindShare(btn) {
            btn.addEventListener('click', async () => {
                const url = btn.dataset.shareUrl || '';
                const title = btn.dataset.shareTitle || document.title;
                const absoluteUrl = url ? new URL(url, window.location.href).href : window.location.href;

                if (navigator.share) {
                    try {
                        await navigator.share({ title, url: absoluteUrl });
                        return;
                    } catch (err) {
                        if (err && err.name === 'AbortError') return; // Nutzer hat abgebrochen
                    }
                }

                if (navigator.clipboard && navigator.clipboard.writeText) {
                    try {
                        await navigator.clipboard.writeText(absoluteUrl);
                        showToast(btn);
                    } catch {
                        /* Clipboard nicht verfügbar – still */
                    }
                }
            });
        }

        root.querySelectorAll('.js-downloadsShare').forEach(bindShare);

        function showToast(btn) {
            const msg = root.dataset.copiedText || btn.getAttribute('data-copied') || 'Link kopiert';
            btn.classList.add('is-copied');
            let toast = btn.querySelector('.downloads__toast');
            if (!toast) {
                toast = document.createElement('span');
                toast.className = 'downloads__toast';
                toast.textContent = msg;
                btn.appendChild(toast);
            }
            requestAnimationFrame(() => toast.classList.add('is-visible'));
            setTimeout(() => {
                toast.classList.remove('is-visible');
                btn.classList.remove('is-copied');
            }, 1600);
        }

        // Lazy Reveal: beim Erreichen des Sentinels naechsten Block einblenden
        if (sentinel && typeof IntersectionObserver !== 'undefined') {
            const observer = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && sentinel.dataset.more === '1') {
                        visibleCount += BLOCK;
                        applyFilter();
                    }
                });
            }, { rootMargin: '300px 0px' });
            observer.observe(sentinel);
        }

        // ── Server-Pagination aufloesen: restliche Seiten nachladen ──────────
        // Der Core paginiert die Kollektion ab 'entries_per_page' Eintraegen
        // (?page=N) und rendert die Pagination direkt hinter die Section - in
        // $collections (und damit im Grid UND in den Sidebar-Filterlisten)
        // steckt dann nur noch die aktuelle Seite. Die uebrigen Seiten werden
        // hier im Hintergrund geladen (gleiches Server-Template -> identisches
        // Markup), Karten und Sidebar-Filter gemergt und die Pagination am
        // Ende entfernt: alles bleibt auf EINER Seite, das Lazy Reveal ueber
        // den Sentinel uebernimmt das Einblenden beim Weiterscrollen.
        // Sequenziell statt parallel, weil so das Ende robust erkannt wird:
        // die erste Seite ohne Karten bricht ab - unabhaengig davon, welche
        // Seitenzahlen der Paginator gerade verlinkt.
        const MAX_PAGES = 50; // Notbremse

        function findPaginationWrap() {
            // echo "<div class='marginTop--small'>" . $pagination . "</div>"
            // steht direkt hinter der Section (frontend_functions.inc.php).
            let el = root.nextElementSibling;
            for (let i = 0; el && i < 4; i++, el = el.nextElementSibling) {
                if (el.querySelector('.pagination')) return el;
            }
            return null;
        }

        function pageUrl(page) {
            const url = new URL(window.location.href);
            if (page > 1) {
                url.searchParams.set('page', String(page));
            } else {
                url.searchParams.delete('page');
            }
            return url.toString();
        }

        // Sidebar-Eintrag (Produktserie/Dokumententyp) einer nachgeladenen
        // Seite mergen: existiert der Key schon, nur data-bereich vereinigen;
        // sonst ans Listenende haengen und Handler binden.
        function mergeSidebar(remote) {
            const serienList = root.querySelector('.downloads__serienList');
            if (serienList) {
                remote.querySelectorAll('.js-downloadsSerieItem').forEach(item => {
                    const box = item.querySelector('.js-downloadsSerie');
                    if (!box) return;
                    const existing = serieItems.find(i => {
                        const b = i.querySelector('.js-downloadsSerie');
                        return b && b.value === box.value;
                    });
                    if (existing) {
                        // Bereich-Tokens vereinigen: dieselbe Serie kann auf
                        // anderen Seiten weitere Bereiche mitbringen.
                        const merged = new Set([
                            ...(existing.dataset.bereich || '').split(';').filter(Boolean),
                            ...(item.dataset.bereich || '').split(';').filter(Boolean),
                        ]);
                        existing.dataset.bereich = Array.from(merged).join(';');
                    } else {
                        serienList.appendChild(item);
                        serieItems.push(item);
                        bindSerieBox(box);
                    }
                });
            }

            const typList = root.querySelector('.downloads__typList');
            if (typList) {
                remote.querySelectorAll('.downloads__typItem').forEach(item => {
                    const box = item.querySelector('.js-downloadsTyp');
                    if (!box) return;
                    const exists = root.querySelector('.js-downloadsTyp[value="' + CSS.escape(box.value) + '"]');
                    if (!exists) {
                        typList.appendChild(item);
                        bindTypBox(box);
                    }
                });
            }
        }

        // Karten + Sidebar einer nachgeladenen Seite in den DOM mergen.
        // Rueckgabe: Anzahl uebernommener Karten (0 = leere Seite = Ende).
        function mergePage(doc, insertRef) {
            const remote = doc.getElementById(root.id);
            if (!remote) return 0;

            const grid = root.querySelector('.js-downloadsGrid');
            const remoteCards = Array.from(remote.querySelectorAll('.js-downloadsCard'));
            remoteCards.forEach(card => {
                grid.insertBefore(card, insertRef);
                card.querySelectorAll('.js-downloadsShare').forEach(bindShare);
            });

            mergeSidebar(remote);

            // cards in DOM-Reihenfolge neu einlesen, damit das Lazy Reveal
            // in der sichtbaren Reihenfolge einblendet.
            cards = Array.from(root.querySelectorAll('.js-downloadsCard'));
            return remoteCards.length;
        }

        async function loadRemainingPages() {
            const paginationWrap = findPaginationWrap();
            if (!paginationWrap) return; // keine Pagination -> Bestand komplett

            const grid = root.querySelector('.js-downloadsGrid');
            if (!grid) return;

            const currentPage = Math.max(1, parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10) || 1);
            // Einfuege-Anker: Seiten VOR der aktuellen kommen vor die erste
            // vorhandene Karte (Deep-Link ?page=N), Seiten danach ans Grid-Ende
            // (vor die Empty-Meldung, die im Grid liegt).
            const firstExisting = cards[0] || emptyMsg;

            try {
                for (let page = 1; page <= MAX_PAGES; page++) {
                    if (page === currentPage) continue;
                    const res = await fetch(pageUrl(page));
                    if (!res.ok) throw new Error('HTTP ' + res.status);
                    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
                    const added = mergePage(doc, page < currentPage ? firstExisting : emptyMsg);
                    if (!added) break; // erste leere Seite = Bestand komplett
                    rebuildSeries();
                    applyFilter();
                }
                // Alles auf einer Seite -> Server-Pagination ist ueberfluessig.
                paginationWrap.remove();
            } catch (err) {
                // Fallback: Pagination stehen lassen (Navigation bleibt moeglich),
                // bereits gemergte Karten bleiben nutzbar.
                console.warn('downloads: Nachladen der Kollektionsseiten fehlgeschlagen', err);
            }
        }

        // Initialzustand ohne Pill-Einflug
        preselectSeriesFromUrl(serieBoxes, activeSeries);
        const initialTab = bereichTabs.find(t => t.classList.contains('is-active')) || bereichTabs[0];
        if (initialTab) {
            activateBereich(initialTab, false);
        } else {
            applyFilter();
        }

        loadRemainingPages();

        // Pill nach Font-Load / Resize neu positionieren (Barlow laedt async)
        const reposition = () => {
            const current = bereichTabs.find(t => t.classList.contains('is-active'));
            if (current) {
                if (pill) pill.style.transition = 'none';
                movePill(current);
                if (pill) requestAnimationFrame(() => { pill.style.transition = ''; });
            }
        };
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(reposition);
        }
        if (typeof ResizeObserver !== 'undefined' && bereichNav) {
            new ResizeObserver(reposition).observe(bereichNav);
        }
    });
}
